import { Controller } from "@hotwired/stimulus";

/**
 * Collapsible group of checkboxes with "All / None" toggles.
 *
 * The content panel animates open/closed using CSS grid rows
 * (`grid-template-rows: 0fr` / `1fr`) for a smooth height transition
 * without needing to measure the content.
 *
 * The content target should be wrapped in a grid container:
 *   <div data-checkbox-group-target="panel" class="grid grid-rows-[0fr]">
 *     <div class="overflow-hidden" data-checkbox-group-target="content">
 *       ...checkboxes...
 *     </div>
 *   </div>
 */
export default class CheckboxGroupController extends Controller {
  static override targets = ["checkbox", "panel", "content", "summary", "icon"];
  declare readonly checkboxTargets: HTMLInputElement[];
  declare readonly panelTarget: HTMLElement;
  declare readonly contentTarget: HTMLElement;
  declare readonly summaryTarget: HTMLElement;
  declare readonly iconTarget: HTMLElement;
  declare readonly hasPanelTarget: boolean;
  declare readonly hasContentTarget: boolean;
  declare readonly hasSummaryTarget: boolean;
  declare readonly hasIconTarget: boolean;

  private expanded = false;

  override connect(): void {
    this.updateSummary();
  }

  toggle(): void {
    if (!this.hasPanelTarget) return;
    this.expanded = !this.expanded;

    if (this.expanded) {
      this.panelTarget.classList.remove("grid-rows-[0fr]");
      this.panelTarget.classList.add("grid-rows-[1fr]");
    } else {
      this.panelTarget.classList.remove("grid-rows-[1fr]");
      this.panelTarget.classList.add("grid-rows-[0fr]");
    }

    if (this.hasIconTarget) {
      this.iconTarget.classList.toggle("rotate-180", this.expanded);
    }
  }

  selectAll(): void {
    for (const cb of this.checkboxTargets) {
      if (!cb.disabled) cb.checked = true;
    }
    this.updateSummary();
  }

  selectNone(): void {
    for (const cb of this.checkboxTargets) {
      if (!cb.disabled) cb.checked = false;
    }
    this.updateSummary();
  }

  changed(): void {
    this.updateSummary();
  }

  private updateSummary(): void {
    if (!this.hasSummaryTarget) return;
    const total = this.checkboxTargets.length;
    const selected = this.checkboxTargets.filter((cb) => cb.checked).length;
    this.summaryTarget.textContent = `${selected} of ${total} selected`;
  }
}
